import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Input, OutlineDangerButton } from '@enechain/ecloud-designsystem'

import { AuditPanel } from '~/components/common/AuditPanel'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { type SaveResult } from '~/pages/CustomerInfo/components/CustomerFormModal'
import { FormGrid, GridLabel } from '~/pages/CustomerInfo/components/FormField'
import { FormModalShell } from '~/pages/CustomerInfo/components/FormModalShell'
import { useReloadCustomerInfo } from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import type {
  Contractor,
  ContractorInput,
} from '~/pages/CustomerInfo/models/types'

type Props = {
  /** 編集対象。undefined = 新規追加。 */
  contractor: Contractor | undefined
  isPending: boolean
  /** 配下に需要家があって削除できない理由 (あれば削除ボタンを押せなくして title に出す)。 */
  deleteBlockReason: string | undefined
  onClose: () => void
  /** 保存。 入力不備の理由を返すと上部に出す (保存できたら undefined)。 競合は呼び出し側が conflict を返す。 */
  onSave: (input: ContractorInput) => Promise<SaveResult>
  /** 編集時のみ。 削除確認ダイアログへ進む。 */
  onDelete: (() => void) | undefined
}

/**
 * 契約者の追加・編集モーダル。 項目: 契約者名 / 請求書の送付先 (郵便番号・住所) / 宛名 (部署・担当者) / 備考。
 * 同名の契約者は実在し得るので重複は禁止しない。 削除は Footer 左端 → 確認ダイアログ
 * (配下に需要家がある間は押せず、 理由は title に出す)。
 * 編集で他の人が先に保存していた時は、 需要家の編集と同じ作法で競合を知らせて再読み込みへ促す。
 */
export const ContractorFormModal = ({
  contractor,
  isPending,
  deleteBlockReason,
  onClose,
  onSave,
  onDelete,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const reload = useReloadCustomerInfo()
  const isNew = contractor === undefined
  const [isDirty, setIsDirty] = useState(false)
  const [name, setName] = useState(contractor?.name ?? '')
  const [postalCode, setPostalCode] = useState(
    contractor?.billingPostalCode ?? '',
  )
  const [address, setAddress] = useState(contractor?.billingAddress ?? '')
  const [attention, setAttention] = useState(contractor?.billingAttention ?? '')
  const [note, setNote] = useState(contractor?.note ?? '')
  const [error, setError] = useState<string | undefined>()
  const [isConflict, setIsConflict] = useState(false)

  const touch = (setter: (value: string) => void) => {
    return (value: string): void => {
      setIsDirty(true)
      setError(undefined)
      setter(value)
    }
  }

  const handleSave = (): void => {
    void (async () => {
      const result = await onSave({
        name,
        billingPostalCode: postalCode,
        billingAddress: address,
        billingAttention: attention,
        note,
      })
      if (result === undefined) return
      if ('conflict' in result) {
        setIsConflict(true)
        return
      }
      setError(result.error)
    })()
  }

  return (
    <>
      <FormModalShell
        errorMessage={error}
        footerStart={
          onDelete !== undefined && !isNew ? (
            <OutlineDangerButton
              disabled={deleteBlockReason !== undefined}
              onClick={onDelete}
              title={
                deleteBlockReason ??
                t('page:customerInfo.contractors.deleteHint')
              }
            >
              {t('common:action.delete')}
            </OutlineDangerButton>
          ) : undefined
        }
        isDirty={isDirty}
        isPending={isPending}
        onClose={onClose}
        onSave={handleSave}
        saveLabel={isNew ? t('common:action.create') : t('common:action.save')}
        title={
          isNew
            ? t('page:customerInfo.contractors.modal.createTitle')
            : t('page:customerInfo.contractors.modal.editTitle')
        }
      >
        <FormGrid>
          <GridLabel htmlFor="customer-info-contractor-field-name">
            {t('page:customerInfo.contractors.field.name')}
          </GridLabel>
          <Input
            id="customer-info-contractor-field-name"
            maxLength={100}
            onChange={(e) => touch(setName)(e.target.value)}
            placeholder={t('page:customerInfo.contractors.field.name')}
            value={name}
          />

          <GridLabel htmlFor="customer-info-contractor-postal-code">
            {t('page:customerInfo.contractors.field.postalCode')}
          </GridLabel>
          <Input
            id="customer-info-contractor-postal-code"
            maxLength={10}
            onChange={(e) => touch(setPostalCode)(e.target.value)}
            placeholder="100-0000"
            value={postalCode}
          />

          <GridLabel htmlFor="customer-info-contractor-address">
            {t('page:customerInfo.contractors.field.address')}
          </GridLabel>
          <Input
            id="customer-info-contractor-address"
            maxLength={200}
            onChange={(e) => touch(setAddress)(e.target.value)}
            value={address}
          />

          <GridLabel htmlFor="customer-info-contractor-attention">
            {t('page:customerInfo.contractors.field.attention')}
          </GridLabel>
          <Input
            id="customer-info-contractor-attention"
            maxLength={100}
            onChange={(e) => touch(setAttention)(e.target.value)}
            value={attention}
          />

          <GridLabel htmlFor="customer-info-contractor-note">
            {t('page:customerInfo.contractors.field.note')}
          </GridLabel>
          <Input
            id="customer-info-contractor-note"
            maxLength={500}
            onChange={(e) => touch(setNote)(e.target.value)}
            value={note}
          />
        </FormGrid>
        {contractor !== undefined && (
          <AuditPanel
            createdAt={contractor.createdAt}
            createdBy={contractor.createdBy}
            updatedAt={contractor.updatedAt}
            updatedBy={contractor.updatedBy}
          />
        )}
      </FormModalShell>
      <ConfirmDialog
        body={t('page:customerInfo.confirm.contractorConflictBody')}
        confirmLabel={t('page:customerInfo.confirm.conflictConfirm')}
        isOpen={isConflict}
        onCancel={() => setIsConflict(false)}
        onConfirm={() => {
          void reload()
          onClose()
        }}
        title={t('page:customerInfo.confirm.conflictTitle')}
      />
    </>
  )
}
